import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios";
import { uploadImageToS3, deleteImageFromS3, getFileExtension } from "../../utils/s3Upload";
import { getRole } from "../../utils/role";

const FollowUpStage2 = ({ reportId, initialData = null, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState({});

  const [formData, setFormData] = useState({
    // Q1: Advisory Given
    advisory_given: "Yes",
    advisory_given_notes: "",
    advisory_given_urls: [],

    // Q2: Farmer Action on Advisory
    farmer_action_status: "Fully Applied",
    farmer_action_notes: "",
    farmer_action_urls: [],

    // Q3: Current Crop Stage
    current_crop_stage: "Vegetative",
    current_crop_stage_notes: "",
    current_crop_stage_urls: [],

    // Q4: Visible Crop Health
    visible_crop_health: "Good",
    visible_crop_health_notes: "",
    visible_crop_health_urls: [],

    // Q5: New Issue Observed
    new_issue_observed: "None",
    new_issue_observed_notes: "",
    new_issue_observed_urls: [],

    // Q6: Next Advisory Due
    next_advisory_date: "",
    next_advisory_notes: "",
    next_advisory_urls: [],

    // Final Remarks
    remarks: ""
  });

  const isReadOnly = Boolean(initialData && Object.keys(initialData).length > 0) && getRole() !== 'SUPPORT';

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const sanitizedData = { ...initialData };

      const urlKeys = [
        "advisory_given_urls",
        "farmer_action_urls",
        "current_crop_stage_urls",
        "visible_crop_health_urls",
        "new_issue_observed_urls",
        "next_advisory_urls"
      ];

      urlKeys.forEach((key) => {
        if (typeof sanitizedData[key] === "string") {
          sanitizedData[key] = [sanitizedData[key]];
        } else if (!Array.isArray(sanitizedData[key])) {
          sanitizedData[key] = [];
        }
      });

      setFormData(sanitizedData);
    }
    setLoading(false);
  }, [initialData]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleMultiPhotoUpload = async (fieldKey, fileList) => {
    if (isReadOnly || !fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    try {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: true }));
      
      const uploadPromises = files.map(async (file) => {
        const fileExt = getFileExtension(file);
        return await uploadImageToS3(file, fileExt, "follow-up-photos");
      });

      const newS3Urls = await Promise.all(uploadPromises);
      const existingUrls = formData[fieldKey] || [];
      handleChange(fieldKey, [...existingUrls, ...newS3Urls]);
    } catch (err) {
      console.error("S3 Multi-Upload Error:", err);
      alert("Failed to upload photos to AWS S3. Please check your network.");
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handlePhotoRemove = async (fieldKey, indexToRemove) => {
    if (isReadOnly) return;
    const urlsArray = formData[fieldKey] || [];
    const fileUrl = urlsArray[indexToRemove];
    if (!fileUrl) return;

    try {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: true }));
      await deleteImageFromS3(fileUrl);
      
      const updatedUrls = urlsArray.filter((_, i) => i !== indexToRemove);
      handleChange(fieldKey, updatedUrls);
    } catch (err) {
      console.error("S3 Deletion Error:", err);
      alert("Could not delete photo from AWS server.");
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    
    if (Object.values(uploadingMedia).some((up) => up)) {
      alert("Please wait for all photo uploads to finish before submitting.");
      return;
    }


    try {
      setSubmitting(true);
      await axiosInstance.post(`/follow-up/response/${reportId}`, {
        follow_up_stage: 2,
        answers: formData
      });

      alert("Stage 2 Mid-Season Progress submitted successfully!");
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit Stage 2.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderMediaWidget = (fieldKey, label, isRequired = false) => {
    const urls = formData[fieldKey] || [];
    const isUploading = uploadingMedia[fieldKey] || false;

    return (
      <div className={`mt-2.5 pt-2.5 border-t border-dashed rounded-md p-3 ${
        isRequired && urls.length === 0 ? "border-amber-300 bg-amber-50/40" : "border-gray-200 bg-purple-50/30"
      }`}>
        <label className="block text-xs font-semibold text-purple-900 mb-1.5">
          {label} {isRequired && <span className="text-red-500 font-bold">*</span>}
        </label>
        
        {!isReadOnly && (
          <input
            type="file"
            multiple
            accept="image/*,.pdf"
            disabled={isUploading}
            onChange={(e) => handleMultiPhotoUpload(fieldKey, e.target.files)}
            className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-purple-100 file:text-purple-700 file:font-semibold hover:file:bg-purple-200 cursor-pointer disabled:opacity-50"
          />
        )}

        {isUploading && (
          <div className="text-xs text-purple-600 font-medium mt-2 flex items-center gap-2">
            <span className="inline-block w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></span>
            Uploading media to AWS S3...
          </div>
        )}

        {urls.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {urls.map((url, idx) => (
              <div key={idx} className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded border border-purple-200 text-xs shadow-sm max-w-full">
                <span className="text-green-600 font-bold shrink-0">✓</span>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline truncate max-w-[140px] sm:max-w-[200px]"
                >
                  {url.split("/").pop()}
                </a>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => handlePhotoRemove(fieldKey, idx)}
                    className="text-red-500 hover:text-red-700 font-bold ml-1 p-0.5 shrink-0"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="p-6 text-center text-gray-500 text-sm">Loading Stage 2 form...</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 text-left">

      {isReadOnly && (
        <div className="bg-blue-50 text-blue-800 text-xs sm:text-sm p-3 rounded-md border border-blue-200 font-medium flex items-center gap-2">
          🔒 This form has already been submitted and is locked for editing.
        </div>
      )}
      
      {/* SECTION 1: ADVISORY & FARMER ACTION */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-5">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">
          1. Advisory & Compliance
        </h4>

        {/* Q1: Advisory Given */}
        <div className="space-y-2">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            1. Advisory Given <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.advisory_given}
              onChange={(e) => handleChange("advisory_given", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            <input
              type="text"
              value={formData.advisory_given_notes}
              onChange={(e) => handleChange("advisory_given_notes", e.target.value)}
              disabled={isReadOnly}
              placeholder="Notes: What advisory was provided?"
              className="w-full sm:col-span-2 border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          {renderMediaWidget("advisory_given_urls", "📷 Optional: Upload Advisory Slip / Prescription", false)}
        </div>

        {/* Q2: Farmer Action on Advisory */}
        <div className="space-y-2 pt-2 border-t border-gray-100">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            2. Farmer Action on Advisory <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.farmer_action_status}
              onChange={(e) => handleChange("farmer_action_status", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Fully Applied">Fully Applied</option>
              <option value="Partially Applied">Partially Applied</option>
              <option value="Not Applied">Not Applied</option>
              <option value="Not Yet Due">Not Yet Due</option>
            </select>
            <input
              type="text"
              value={formData.farmer_action_notes}
              onChange={(e) => handleChange("farmer_action_notes", e.target.value)}
              disabled={isReadOnly}
              placeholder="Notes: Why was it partially applied / skipped?"
              className="w-full sm:col-span-2 border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          {renderMediaWidget("farmer_action_urls", "📷 Optional: Upload Photo of Application / Field Compliance", false)}
        </div>
      </div>

      {/* SECTION 2: CROP HEALTH & OBSERVATIONS */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-5">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">
          2. Crop Stage & Health Observations
        </h4>

        {/* Q3: Current Crop Stage */}
        <div className="space-y-2">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            3. Current Crop Stage <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.current_crop_stage}
              onChange={(e) => handleChange("current_crop_stage", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Sowing">Sowing</option>
              <option value="Vegetative">Vegetative</option>
              <option value="Flowering">Flowering</option>
              <option value="Fruiting">Fruiting</option>
              <option value="Maturity">Maturity</option>
              <option value="Harvested">Harvested</option>
            </select>
            <input
              type="text"
              value={formData.current_crop_stage_notes}
              onChange={(e) => handleChange("current_crop_stage_notes", e.target.value)}
              disabled={isReadOnly}
              placeholder="Notes: Specific observations about crop stage..."
              className="w-full sm:col-span-2 border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          {renderMediaWidget("current_crop_stage_urls", "📷 Optional: Upload Current Crop Stage Photo", false)}
        </div>

        {/* Q4: Visible Crop Health */}
        <div className="space-y-2 pt-2 border-t border-gray-100">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            4. Visible Crop Health <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.visible_crop_health}
              onChange={(e) => handleChange("visible_crop_health", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Good">Good</option>
              <option value="Average">Average</option>
              <option value="Poor">Poor</option>
            </select>
            <input
              type="text"
              value={formData.visible_crop_health_notes}
              onChange={(e) => handleChange("visible_crop_health_notes", e.target.value)}
              disabled={isReadOnly}
              placeholder="Notes: Detail any yellowing, stunted growth, etc."
              className="w-full sm:col-span-2 border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          {renderMediaWidget("visible_crop_health_urls", "📷 Optional: Upload Visible Crop Health Photo", false)}
        </div>

        {/* Q5: New Issue Observed */}
        <div className="space-y-2 pt-2 border-t border-gray-100">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            5. New Issue Observed <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={formData.new_issue_observed}
              onChange={(e) => handleChange("new_issue_observed", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="None">None</option>
              <option value="Disease">Disease</option>
              <option value="Pest">Pest</option>
              <option value="Nutrient Deficiency">Nutrient Deficiency</option>
              <option value="Water Stress">Water Stress</option>
            </select>
            <input
              type="text"
              value={formData.new_issue_observed_notes}
              onChange={(e) => handleChange("new_issue_observed_notes", e.target.value)}
              disabled={isReadOnly}
              placeholder="Notes: Specify disease/pest name or deficiency symptoms..."
              className="w-full sm:col-span-2 border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          {renderMediaWidget("new_issue_observed_urls", "📷 Optional: Upload Photo of Issue / Pest (Even if None observed, upload general field photo)", false)}
        </div>
      </div>

      {/* SECTION 3: FUTURE ADVISORY PLANNING */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">
          3. Next Advisory Planning
        </h4>

        {/* Q6: Next Advisory Due (Date + Free Text + Notes) */}
        <div className="space-y-3">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700">
            6. Next Advisory Due Date & Type {!isReadOnly && <span className="text-red-500">*</span>}
          </label>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <span className="block text-[11px] text-gray-500 mb-1">Target Date</span>
              <input
                type="date"
                value={formData.next_advisory_date}
                onChange={(e) => handleChange("next_advisory_date", e.target.value)}
                disabled={isReadOnly}
                required
                className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
              />
            </div>
          </div>

          <input
            type="text"
            value={formData.next_advisory_notes}
            onChange={(e) => handleChange("next_advisory_notes", e.target.value)}
            disabled={isReadOnly}
            placeholder="Notes: What specific instructions should be prepared for the next visit?"
            className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
          />
          {renderMediaWidget("next_advisory_urls", "📷 Optional: Upload Supporting Documentation for Next Visit", false)}
        </div>
      </div>

      {/* --- SECTION 4: REMARKS --- */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm">
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
          General Remarks / Additional Field Notes
        </label>
        <textarea
          rows={3}
          value={formData.remarks}
          onChange={(e) => handleChange("remarks", e.target.value)}
          disabled={isReadOnly}
          placeholder="Add any general observations, farmer feedback, or summary remarks..."
          className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
        />
      </div>

      {!isReadOnly && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={submitting || Object.values(uploadingMedia).some((up) => up)}
            className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-md hover:bg-purple-700 disabled:opacity-50 transition shadow-sm"
          >
            {submitting ? "Saving Stage 3..." : initialData ? "Update Stage 2" : "Submit Stage 2"}
          </button>
        </div>
      )}
    </form>
  );
};

export default FollowUpStage2;